import { PrcOrder } from './prc-order.model';

export interface QuantityComparison {
  umb: number;  // Diferencia en UMB
  ump: number;  // Diferencia en UMP
  umv: number;  // Diferencia en UMV
  percentage: number; // Porcentaje de pérdida
}

export interface LostSaleCalculations {
  commercialLostSale: {
    desiredVsSold: QuantityComparison;      // Deseada - Vendida
    soldVsConfirmed: QuantityComparison;    // Vendida - Confirmada
  };
  logisticLostSale: QuantityComparison;     // Vendida - Recibida
  realLostSale: QuantityComparison;         // Vendida - Facturada
}

export interface LostSaleItem extends PrcOrder {
  calculations: LostSaleCalculations;
  unitOfMeasure: {
    base: {
      id: string;
      name: string;
      code: string;
    };
    parallel: {
      id: string;
      name: string;
      code: string;
    };
    sale: {
      id: string;
      name: string;
      code: string;
    };
  };
  trip?: {
    id: number;
    code: string;
    status: string;
    date: Date;
  };
  material: {
    id: string;
    name: string;
    code: string;
    groupId: string;
    groupName: string;
  };
  customer: {
    id: number;
    name: string;
    code: string;
    address: string;
    zone: string;
  };
  route: {
    id: number;
    name: string;
    code: string;
  };
  subRoute?: {
    id: number;
    name: string;
    code: string;
    responsibleCode: string;
    responsibleName: string;
  };
  user: string;
  // Missing fields from original report
  serials: string;                      // Número de pedido/serie
  stockAtCreation: number;              // Stock al momento (UMB)
  stockAtCreationParallel: number;      // Stock al momento (UMP)
  // Additional properties for API compatibility
  customerCode?: string;
  materialCode?: string;
  materialGroupName?: string;
  // FS-37: Oficina de Venta, Grupo de Vendedor y Zona de Venta
  salesOfficeName?: string;
  salesOfficeCode?: string;
  salesGroup?: string;
  salesZone?: string;
}

export interface LostSaleSummaryItem {
  materialId: string;
  materialName: string;
  materialCode: string;
  materialGroupId: string;
  materialGroupName: string;
  customerId: number;
  customerName: string;
  customerCode: string;

  // Totales agregados
  totalSoldOriginalQuantityUMB: number;
  totalSoldOriginalQuantityUMP: number;
  totalSoldQuantityUMB: number;
  totalSoldQuantityUMP: number;
  totalConfirmedQuantityUMB: number;
  totalConfirmedQuantityUMP: number;
  totalDeliveredQuantityUMB: number;
  totalDeliveredQuantityUMP: number;
  totalInvoicedQuantityUMB: number;
  totalInvoicedQuantityUMP: number;

  // Cálculos de venta perdida
  commercialLostSaleUMB: number;
  commercialLostSaleUMP: number;
  logisticLostSaleUMB: number;
  logisticLostSaleUMP: number;
  realLostSaleUMB: number;
  realLostSaleUMP: number;

  // Porcentajes
  commercialLostSalePercentage: number;
  logisticLostSalePercentage: number;
  realLostSalePercentage: number;

  ordersCount: number;

  // Missing fields from original report
  numberOrder: string;                    // Número de pedido
  invoiceDocument: string;                // Número de factura
  sellerToName: string;                   // Responsable/Vendedor
  sellerToCode: string;                   // Código del responsable
  locationTwo: string;                    // Ubicación (provincia/municipio)
  // Monetary values in local currency
  sumSoldQuantity: number;                // Suma pedido L (moneda local)
  sumStockConfirmedERP: number;           // Suma reserva L (moneda local)
  sumDeliveredQuantity: number;           // Suma entrega L (moneda local)
  sumInvoiceQuantity: number;             // Suma factura L (moneda local)
  lostSaleAmount: number;                 // Diferencia en moneda local
  currency: string;                       // Código de moneda

  // Units of measure mapping
  unitOfMeasure?: {
    base: {
      id: string;
      name: string;
      code: string;
    };
    parallel: {
      id: string;
      name: string;
      code: string;
    };
    sale: {
      id: string;
      name: string;
      code: string;
    };
  };
}

export interface LostSaleFilters {
  startDate: Date;
  endDate: Date;
  companyId?: number;
  salesOfficeId?: number;
  subRouteId?: number[];
  assignedUserId?: string[];
  warehouseId?: number[];
  materialGroupId?: string[];
  materialId?: string;
  customerId?: number[];
  tripId?: number;
  documentStatus?: string[];
  hasLostSale?: boolean;
  // FS-37: filtros por Grupo de Vendedor y Zona de Venta (Campos de Usuario de la Cadena de Distribución del soldTo)
  salesGroup?: string[];
  salesZone?: string[];
}

export interface LostSaleResponse {
  status: string;
  statusCode: string;
  data: [{
    data: LostSaleItem[];
    pageInfo: ({
      _id: null;
      total: number;
    })[];
  }];
}

export interface LostSaleSummaryResponse {
  status: string;
  statusCode: string;
  data: [{
    data: LostSaleSummaryItem[];
    pageInfo: {
      _id: null;
      total: number;
    };
  }];
  totals?: {
    totalCommercialLostSaleUMB: number;
    totalCommercialLostSaleUMP: number;
    totalLogisticLostSaleUMB: number;
    totalLogisticLostSaleUMP: number;
    totalRealLostSaleUMB: number;
    totalRealLostSaleUMP: number;
  };
}

export interface LostSaleQueryParams {
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  search?: string;
  filters: LostSaleFilters;
}

export enum LostSaleTabType {
  COMMERCIAL = 'commercial',
  SUMMARY = 'summary'
}

export interface LostSaleTableColumn {
  key: string;
  label: string;
  sortable: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  format?: 'number' | 'date' | 'currency' | 'percentage';
  sticky?: boolean;
}